/**
 * Bottom-up assessment of one KEV hit (DEV-100): component → product versions →
 * instances, each with its own VEX status.
 *
 * - The component verdict is only a proposal for the product versions that
 *   contain the component version.
 * - A confirmed product verdict AFFECTED creates the customer notifications of
 *   every instance running that version. The evidence of a confirmed AFFECTED
 *   or FIXED version is kept here; the customer mails are sent from the
 *   separate Benachrichtigungen page.
 * - The component verdict can be taken over for all product versions at once
 *   (DEV-130); a confirmed own verdict of a version is never overwritten.
 * - An instance follows the confirmed verdict of the version it runs now — no
 *   confirmation needed. Only a deliberate deviation is recorded (environment
 *   reasons are allowed there), with environment factors and a priority.
 * - The first confirmed AFFECTED opens the vulnerability case of the product
 *   (DEV-132): core facts, CRA reporting clocks and their submission stamps,
 *   and the Security Advisory of the product (DEV-134).
 *
 * The server owns every rule (422 with a German detail); the forms mirror them
 * only to say why saving is disabled.
 */
import type { MessageDescriptor } from '@lingui/core';
// Core `t` is aliased: the components below take their own `t` from useLingui().
import { msg, plural, t as translate } from '@lingui/core/macro';
import { Trans, useLingui } from '@lingui/react/macro';
import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';

import Banner from '../../components/Banner';
import Chip, { ChipList } from '../../components/Chip';
import { CveTitleLink } from '../../components/CveLink';
import Modal from '../../components/Modal';
import Spinner, { PageSpinner } from '../../components/Spinner';
import { useToast } from '../../components/Toast';
import {
  adoptComponentAssessment,
  clearInstanceAssessment,
  errorMessage,
  fetchEventAssessment,
  submitComponentAssessment,
  submitInstanceAssessment,
  submitProductAssessment,
} from '../../lib/api';
import { fmtDate } from '../../lib/format';
import { missingPermissionTitle } from '../../lib/permissions';
import type {
  AdoptResult,
  AssessmentPriority,
  AssessmentStatus,
  CmdbDateTime,
  EventAssessmentDetail,
  InstanceAssessmentRow,
  ProductAssessmentRow,
  ProductVerdictResult,
  ReportStage,
  VexJustification,
  VulnerabilityCase,
  VulnerabilityEventRow,
} from '../../lib/types';

import KeepHeight from '../../components/KeepHeight';
import { nextOpenProduct } from './assessmentModel';
import { CaseTab, type CaseView } from './CasePanel';
import { CaseStepsLine } from './CaseStepper';
import type { CaseSection } from './caseSteps';
import { EventCveFacts, EventScopeFacts } from './EventFacts';
import ProductDocuments from './ProductDocuments';
import {
  JUSTIFICATION_LABELS,
  PRIORITY_LABELS,
  PRIORITY_TONE,
  STATUS_LABELS,
  STATUS_TONE,
  komponenteLabel,
  verdictProblem,
} from './eventHelpers';

export type AssessmentTab = 'component' | 'products' | 'case' | 'instances';

type Level = 'component' | 'product' | 'instance';

const STATUSES: Readonly<Record<Level, readonly AssessmentStatus[]>> = {
  component: ['UNDER_INVESTIGATION', 'AFFECTED', 'NOT_AFFECTED'],
  product: ['UNDER_INVESTIGATION', 'AFFECTED', 'NOT_AFFECTED', 'FIXED'],
  instance: ['UNDER_INVESTIGATION', 'AFFECTED', 'NOT_AFFECTED'],
};

const JUSTIFICATIONS = Object.keys(JUSTIFICATION_LABELS) as VexJustification[];

/** The free-text reason per level; on instances it stays internal (DEV-128). */
const COMMENT_LABELS: Readonly<Record<Level, MessageDescriptor>> = {
  component: msg`Erläuterung`,
  product: msg`Begründung`,
  instance: msg`Begründung (intern)`,
};

const COMMENT_PLACEHOLDERS: Readonly<Record<Level, MessageDescriptor>> = {
  component: msg`Pflicht bei „Nicht betroffen“.`,
  product: msg`Pflicht bei „Nicht betroffen“ und bei Abweichung vom Vorschlag. Keine umgebungsbezogenen Gründe.`,
  instance: msg`Pflicht bei „Nicht betroffen“ und bei Abweichung von der Produktbewertung.`,
};

export interface AssessmentDialogProps {
  event: VulnerabilityEventRow;
  bewerter: string;
  canWrite: boolean;
  initialTab?: AssessmentTab;
  /** Product version to show first, e.g. from a link of the Benachrichtigungen page. */
  initialVersionId?: number | null;
  /** Authority report to open in the case tab, e.g. from the deadline view (DEV-60). */
  initialReport?: ReportStage | null;
  onClose: () => void;
  /** Any verdict was saved: the queue row has to be refetched. */
  onChanged: () => void;
}

function productLabel(product: ProductAssessmentRow): string {
  const versionId = product.version_id;
  return [product.product_name, product.version_nr].filter(Boolean).join(' ') || translate`Produktversion #${versionId}`;
}

/** A confirmed AFFECTED version whose case advisory is not released: no customer mail yet (DEV-134). */
function advisoryOpen(product: ProductAssessmentRow): boolean {
  return product.confirmed && product.effective_status === 'AFFECTED' && product.advisory_status !== 'RELEASED';
}

function advisoryOpenLabel(product: ProductAssessmentRow): string {
  return product.advisory_status === 'IN_PROGRESS' ? translate`Advisory in Arbeit` : translate`Advisory fehlt`;
}

/**
 * The tile is tinted by its verdict: affected red, not affected or fixed green.
 * The open product shows the status chosen in its form at once (`chosen`), the
 * others their confirmed verdict; a mere proposal stays neutral.
 */
function verdictTint(product: ProductAssessmentRow, chosen: AssessmentStatus | null): string {
  const status = chosen ?? (product.confirmed ? product.effective_status : null);
  if (status === 'AFFECTED') return ' is-affected';
  return status === 'NOT_AFFECTED' || status === 'FIXED' ? ' is-clear' : '';
}

/** Instances and notification progress of a version in the list. */
function productListHint(product: ProductAssessmentRow): string {
  const instances = translate`${plural(product.instance_count, { one: '# Instanz', other: '# Instanzen' })}`;
  const { sent, total } = product.notifications;
  return total ? translate`${instances} · ${sent}/${total} benachrichtigt` : instances;
}

/** The save button of a product verdict: confirm or save, and whether the next version follows. */
function productSaveLabel(confirmed: boolean, hasNext: boolean): string {
  if (confirmed) return hasNext ? translate`Produktbewertung speichern und weiter` : translate`Produktbewertung speichern`;
  return hasNext ? translate`Bestätigen und weiter` : translate`Bestätigen`;
}

/** When and by whom a verdict was last saved. */
function AssessedLine({ at, by }: Readonly<{ at: CmdbDateTime; by: string | null }>): ReactNode {
  const { t } = useLingui();
  const when = fmtDate(at);
  const who = by ?? t`unbekannt`;
  return <p className="hint"><Trans>Zuletzt {when} · {who}</Trans></p>;
}

function productNeedsAttention(product: ProductAssessmentRow): boolean {
  return product.needs_review || product.contradiction || !product.confirmed || advisoryOpen(product);
}

/** After a verdict was saved only pending uploads are left (they are saved on their own). */
function keepDocumentEdits(current: ReadonlySet<string>): ReadonlySet<string> {
  return current.has('documents') ? new Set(['documents']) : new Set();
}

function StatusChip({ status, confirmed = true, inherited = false }: Readonly<{
  status: AssessmentStatus;
  confirmed?: boolean;
  /** An instance following the confirmed product verdict (DEV-130). */
  inherited?: boolean;
}>): ReactNode {
  const { t } = useLingui();
  const settled = confirmed || inherited;
  const label = STATUS_LABELS[status];
  let text = t`Vorschlag: ${label}`;
  if (settled) text = inherited ? t`${label} (von der Produktversion)` : label;
  return (
    <Chip tone={settled ? STATUS_TONE[status] : 'neutral'} title={settled ? undefined : t`Vorschlag, noch nicht bestätigt`}>
      {text}
    </Chip>
  );
}

/** The toast after a product verdict was saved. */
function productSavedMessage(result: ProductVerdictResult): string {
  const status = STATUS_LABELS[result.status];
  const parts = [translate`Produktbewertung gespeichert: ${status}.`];
  if (result.notifications) {
    const { created, existing } = result.notifications;
    parts.push(translate`${plural(created, { one: '# Benachrichtigung', other: '# Benachrichtigungen' })} angelegt, ${existing} vorhanden.`);
  }
  if (result.notifications_removed) {
    parts.push(translate`${plural(result.notifications_removed, { one: '# offene Benachrichtigung', other: '# offene Benachrichtigungen' })} entfernt — es war noch nichts versendet.`);
  }
  if (result.marked_for_review) {
    parts.push(translate`${plural(result.marked_for_review, { one: '# Instanzbewertung', other: '# Instanzbewertungen' })} zur erneuten Prüfung markiert.`);
  }
  return parts.join(' ');
}

/** The confirmation question before the component verdict is taken over (DEV-130). */
function adoptQuestion(preview: AdoptResult): string {
  const status = STATUS_LABELS[preview.status];
  const lines = [
    translate`„${status}“ für ${plural(preview.adopted.length, { one: '# Produktversion', other: '# Produktversionen' })} übernehmen?`,
    '',
    ...preview.adopted.map((version) => `• ${version.label}`),
  ];
  if (preview.skipped.length > 0) {
    lines.push('', translate`Übersprungen:`, ...preview.skipped.map((version) => `• ${version.label} — ${version.reason}`));
  }
  if (preview.status === 'AFFECTED') {
    lines.push('', translate`Für diese Produktversionen werden die Kundenbenachrichtigungen angelegt.`);
  }
  return lines.join('\n');
}

/** Save the component verdict, show what would be taken over, then take it over. */
async function saveAndAdopt(eventId: number, values: VerdictValues): Promise<string> {
  const saved = await submitComponentAssessment(eventId, values);
  const status = STATUS_LABELS[saved.status];
  const head = translate`Komponentenbewertung gespeichert: ${status}.`;
  const preview = await adoptComponentAssessment(eventId, true);
  if (preview.adopted.length === 0) {
    const reasons = preview.skipped.map((version) => `${version.label}: ${version.reason}`).join('; ');
    const tail = reasons ? translate`Keine Produktversion zu übernehmen (${reasons}).` : translate`Keine Produktversion zu übernehmen.`;
    return `${head} ${tail}`;
  }
  if (!window.confirm(adoptQuestion(preview))) {
    const declined = translate`Nicht für die Produktversionen übernommen.`;
    return `${head} ${declined}`;
  }
  const result = await adoptComponentAssessment(eventId, false);
  const adopted = result.adopted.length;
  const skippedCount = result.skipped.length;
  const tail = skippedCount > 0
    ? translate`Übernommen für ${plural(adopted, { one: '# Produktversion', other: '# Produktversionen' })}, ${skippedCount} übersprungen.`
    : translate`Übernommen für ${plural(adopted, { one: '# Produktversion', other: '# Produktversionen' })}.`;
  return `${head} ${tail}`;
}

/* ------------------------------------------------------------------------- *
 * Verdict form — shared by all three levels
 * ------------------------------------------------------------------------- */

export interface VerdictValues {
  status: AssessmentStatus;
  justification: VexJustification | null;
  comment: string;
}

interface VerdictFormProps {
  level: Level;
  /** Pre-selected: the stored verdict, otherwise the proposal. */
  initial: VerdictValues;
  proposal: AssessmentStatus;
  canWrite: boolean;
  saving: boolean;
  saveLabel: string;
  /** Extra fields (instance level) — part of the same form; may depend on the chosen status. */
  children?: ReactNode | ((status: AssessmentStatus) => ReactNode);
  /** The extra fields were changed (counts as unsaved). */
  extraDirty?: boolean;
  /** Saving without a change is meaningful: confirming a proposal or a review. */
  saveUnchanged?: boolean;
  /** More buttons next to the save button; `blocked` when saving is not possible now. */
  renderActions?: (values: VerdictValues, blocked: boolean) => ReactNode;
  onSave: (values: VerdictValues) => void;
  onDirtyChange: (dirty: boolean) => void;
  /** The status chosen in the form, saved or not (the product tile previews it). */
  onStatusChange?: (status: AssessmentStatus) => void;
}

function VerdictForm({
  level, initial, proposal, canWrite, saving, saveLabel, children, extraDirty = false, saveUnchanged = false,
  renderActions, onSave, onDirtyChange, onStatusChange,
}: Readonly<VerdictFormProps>): ReactNode {
  const { t, i18n } = useLingui();
  const [values, setValues] = useState<VerdictValues>(initial);
  useEffect(() => onStatusChange?.(values.status), [values.status, onStatusChange]);
  const dirty = extraDirty || values.status !== initial.status
    || values.justification !== initial.justification || values.comment !== initial.comment;
  useEffect(() => onDirtyChange(dirty), [dirty, onDirtyChange]);
  const problem = verdictProblem(level, values.status, values.justification, values.comment, proposal);
  const blocked = !canWrite || saving || problem !== null;
  const justifications = level === 'instance' ? JUSTIFICATIONS : JUSTIFICATIONS.filter((j) => j !== 'ENVIRONMENT');
  const idBase = `verdict-${level}`;

  return (
    <form
      className="assessment-form"
      onSubmit={(formEvent) => {
        formEvent.preventDefault();
        if (problem === null && canWrite && !saving) onSave(values);
      }}
    >
      <fieldset disabled={!canWrite || saving}>
        <legend><Trans>Status</Trans></legend>
        <div className="seg assessment-status-choice" role="radiogroup" aria-label={t`Status`}>
          {STATUSES[level].map((status) => (
            <button
              key={status}
              type="button"
              role="radio"
              aria-checked={values.status === status}
              className={values.status === status ? 'on' : undefined}
              onClick={() => setValues((current) => ({
                ...current, status, justification: status === 'NOT_AFFECTED' ? current.justification : null,
              }))}
            >
              {STATUS_LABELS[status]}
            </button>
          ))}
        </div>
        {level !== 'component' && proposal !== 'OPEN' ? (
          <p className="hint assessment-proposal">
            <Trans>Vorschlag:</Trans> <StatusChip status={proposal} />
            {values.status !== proposal && STATUSES[level].includes(proposal) ? (
              <button type="button" className="link" onClick={() => setValues((c) => ({ ...c, status: proposal }))}>
                <Trans>übernehmen</Trans>
              </button>
            ) : null}
          </p>
        ) : null}
        {values.status === 'NOT_AFFECTED' ? (
          <div className="field">
            <label htmlFor={`${idBase}-justification`}><Trans>VEX-Begründung *</Trans></label>
            <select
              id={`${idBase}-justification`}
              value={values.justification ?? ''}
              onChange={(e) => setValues((c) => ({ ...c, justification: (e.target.value || null) as VexJustification | null }))}
            >
              <option value="">{t`— bitte wählen —`}</option>
              {justifications.map((j) => <option key={j} value={j}>{JUSTIFICATION_LABELS[j]}</option>)}
            </select>
          </div>
        ) : null}
        <div className="field">
          <label htmlFor={`${idBase}-comment`}>
            {i18n._(COMMENT_LABELS[level])}
            {values.status === 'NOT_AFFECTED' ? ' *' : ''}
          </label>
          <textarea
            id={`${idBase}-comment`}
            rows={3}
            value={values.comment}
            onChange={(e) => setValues((c) => ({ ...c, comment: e.target.value }))}
            placeholder={i18n._(COMMENT_PLACEHOLDERS[level])}
          />
        </div>
        {typeof children === 'function' ? children(values.status) : children}
      </fieldset>
      <div className="assessment-form-actions">
        {problem && (dirty || saveUnchanged) ? <span className="hint" role="status">{problem}</span> : null}
        <div className="spacer" />
        {renderActions?.(values, blocked)}
        <button
          type="submit"
          className="primary"
          disabled={blocked || (!dirty && !saveUnchanged)}
          title={!canWrite ? missingPermissionTitle('triage.write') : problem ?? undefined}
        >
          {saving ? <Spinner variant="on-accent" /> : null}{saveLabel}
        </button>
      </div>
    </form>
  );
}

/* ------------------------------------------------------------------------- *
 * Dialog
 * ------------------------------------------------------------------------- */

export function AssessmentDialog({
  event, bewerter, canWrite, initialTab = 'component', initialVersionId = null, initialReport = null, onClose,
  onChanged,
}: AssessmentDialogProps): ReactNode {
  const { t } = useLingui();
  const toast = useToast();
  const [detail, setDetail] = useState<EventAssessmentDetail | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [tab, setTab] = useState<AssessmentTab>(initialTab);
  const [versionId, setVersionId] = useState<number | null>(initialVersionId);
  // The product whose vulnerability case the case tab shows (DEV-134).
  const [caseProductTypeId, setCaseProductTypeId] = useState<number | null>(null);
  // Work area and report stage per case (DEV-103): kept across reloads, and set
  // when a step below a product verdict opens the case at that very place.
  const [caseViews, setCaseViews] = useState<Readonly<Record<number, CaseView>>>({});
  // The status chosen in the open product's form, saved or not: its tile previews it.
  const [chosenStatus, setChosenStatus] = useState<AssessmentStatus | null>(null);
  const [instanceId, setInstanceId] = useState<number | null>(null);
  const [saving, setSaving] = useState(false);
  const [dirtyParts, setDirtyParts] = useState<ReadonlySet<string>>(new Set());
  const [confirmClose, setConfirmClose] = useState(false);
  // Remounts the forms with the server state after every save.
  const [revision, setRevision] = useState(0);

  const cve = event.cve.id ?? detail?.cve ?? '';

  const load = useCallback(async (): Promise<void> => {
    try {
      const fresh = await fetchEventAssessment(event.id);
      setDetail(fresh);
      setLoadError(null);
      setVersionId((current) => current ?? fresh.products[0]?.version_id ?? null);
      setCaseProductTypeId((current) => current
        ?? fresh.products.find((p) => p.version_id === initialVersionId)?.product_type_id ?? null);
    } catch (err) {
      setLoadError(errorMessage(err));
    }
  }, [event.id, initialVersionId]);

  useEffect(() => { void load(); }, [load]);

  const markDirty = useCallback((part: string, dirty: boolean) => {
    setDirtyParts((current) => {
      if (current.has(part) === dirty) return current;
      const next = new Set(current);
      if (dirty) next.add(part); else next.delete(part);
      return next;
    });
  }, []);
  const dirtyHandlers = useMemo(() => ({
    component: (d: boolean) => markDirty('component', d),
    product: (d: boolean) => markDirty('product', d),
    instance: (d: boolean) => markDirty('instance', d),
    documents: (d: boolean) => markDirty('documents', d),
    case: (d: boolean) => markDirty('case', d),
  }), [markDirty]);
  const unsaved = canWrite && dirtyParts.size > 0;

  const instances = useMemo(
    () => (detail?.products ?? []).flatMap((p) => p.instances.map((i) => ({ instance: i, product: p }))),
    [detail],
  );
  // A version handed in from outside (a link) may not belong to this hit: then the
  // first one — with a single version there is no list to choose from.
  const product = detail?.products.find((p) => p.version_id === versionId) ?? detail?.products[0] ?? null;
  const openVersionId = product?.version_id ?? null;
  const selectedInstance = instances.find((row) => row.instance.instance_id === instanceId)
    ?? instances[0] ?? null;
  const cases = detail?.cases ?? [];
  // DEV-103: after a confirmation the next open version is selected.
  const nextProduct = product && detail ? nextOpenProduct(detail.products, product.version_id) : null;
  const openProductLabel = product ? productLabel(product) : '';
  const reviewReason = product?.review_reason ?? t`eine Komponentenbewertung hat sich geändert`;
  const effectiveLabel = product ? STATUS_LABELS[product.effective_status] : '';

  /** Runs a save; true when it went through. */
  async function run(action: () => Promise<string>): Promise<boolean> {
    setSaving(true);
    setSaveError(null);
    try {
      const message = await action();
      toast.success(message);
      setDirtyParts(keepDocumentEdits);
      await load();
      setRevision((r) => r + 1);
      onChanged();
      return true;
    } catch (err) {
      setSaveError(errorMessage(err));
      return false;
    } finally {
      setSaving(false);
    }
  }

  function requestClose(): void {
    if (saving) return;
    if (unsaved) setConfirmClose(true);
    else onClose();
  }

  const reviewMark = (needsReview: boolean, reason: string | null): ReactNode => needsReview ? (
    <Chip tone="warn" title={reason ?? undefined}><Trans>Erneut prüfen</Trans></Chip>
  ) : null;

  const tabs: Array<{ id: AssessmentTab; label: string; count?: number; attention?: boolean }> = [
    { id: 'component', label: t`Komponente` },
    {
      id: 'products', label: t`Produktversionen`, count: detail?.products.length,
      attention: detail?.products.some(productNeedsAttention),
    },
    {
      id: 'case', label: t`Schwachstellenfall`, count: cases.length,
      attention: cases.some((item) => item.clocks.some((c) => c.state === 'overdue' || c.state === 'due_soon')),
    },
    {
      id: 'instances', label: t`Instanzen`, count: instances.length,
      attention: instances.some(({ instance }) => instance.needs_review || instance.version_changed),
    },
  ];
  const eventId = event.id;
  const component = komponenteLabel(event);

  return (
    <Modal
      title={<><Trans>Triage</Trans> <span className="muted">/</span> <CveTitleLink cve={event.cve.id} /></>}
      subtitle={t`Event #${eventId} · ${component}`}
      onClose={requestClose}
      size="wide"
      dismissable={!saving}
      footer={confirmClose ? (
        <div className="triage-close-confirm" role="alert">
          <div>
            <strong><Trans>Ungespeicherte Änderungen verwerfen?</Trans></strong>
            <p><Trans>Gespeicherte Bewertungen, Entwürfe und Anhänge bleiben erhalten.</Trans></p>
          </div>
          <button type="button" className="ghost" onClick={() => setConfirmClose(false)}><Trans>Weiter bearbeiten</Trans></button>
          <button type="button" className="danger" onClick={onClose}><Trans>Änderungen verwerfen</Trans></button>
        </div>
      ) : (
        <>
          <span className="triage-footer-note">
            {canWrite ? t`Bewertet von ${bewerter}` : t`Nur lesend`}
          </span>
          <div className="spacer" />
          <button type="button" className="ghost" onClick={requestClose} disabled={saving}><Trans>Schließen</Trans></button>
        </>
      )}
    >
      <div className="triage-workspace" inert={confirmClose}>
        <div className="tabs triage-workspace-tabs" role="tablist" aria-label={t`Bewertungsebenen`}>
          {tabs.map((item) => (
            <button
              key={item.id}
              type="button"
              role="tab"
              id={`assessment-tab-${item.id}`}
              aria-controls={`assessment-panel-${item.id}`}
              aria-selected={tab === item.id}
              tabIndex={tab === item.id ? 0 : -1}
              onClick={() => setTab(item.id)}
            >
              {item.label}
              {item.count !== undefined ? <Chip>{item.count}</Chip> : null}
              {item.attention ? <span className="triage-unsaved-dot" aria-label={t`Handlungsbedarf`} /> : null}
            </button>
          ))}
        </div>

        {saveError ? (
          <Banner kind="error" sub={t`Nichts wurde gespeichert.`} onDismiss={() => setSaveError(null)}>{saveError}</Banner>
        ) : null}
        {loadError ? (
          <Banner kind="error" sub={<button type="button" className="ghost" onClick={() => void load()}><Trans>Erneut laden</Trans></button>}>
            <Trans>Bewertung konnte nicht geladen werden: {loadError}</Trans>
          </Banner>
        ) : null}
        {detail === null && loadError === null ? <PageSpinner text={t`Bewertung wird geladen…`} /> : null}

        {detail !== null ? (
          <>
            {/* --- Component --------------------------------------------- */}
            <div role="tabpanel" id="assessment-panel-component" aria-labelledby="assessment-tab-component" hidden={tab !== 'component'}>
              <section className="triage-doc-panel">
                <div className="triage-panel-heading">
                  <h3><Trans>Komponentenbewertung</Trans></h3>
                  <StatusChip status={detail.component.status} />
                </div>
                <p className="hint">
                  {t`Die Bewertung gilt für die Komponentenversion und ist ein Vorschlag für ${plural(detail.products.length, { one: '# Produktversion', other: '# Produktversionen' })}, die sie enthalten. Mit „für alle Produktversionen übernehmen“ wird sie dort bestätigt; die Instanzen folgen ihrer Produktversion. Benachrichtigt wird erst nach der Produktbewertung.`}
                </p>
                {detail.component.closed_by_scan ? (
                  <Banner kind="info"><Trans>Vom KEV-Scan geschlossen: Die Version ist nicht mehr im Feld oder die CVE wird nicht mehr gemeldet.</Trans></Banner>
                ) : null}
                {detail.component.assessed_at ? (
                  <AssessedLine at={detail.component.assessed_at} by={detail.component.assessed_by} />
                ) : null}
                <VerdictForm
                  key={`component-${revision}`}
                  level="component"
                  initial={{
                    status: detail.component.status === 'OPEN' ? 'UNDER_INVESTIGATION' : detail.component.status,
                    justification: detail.component.justification,
                    comment: detail.component.comment ?? '',
                  }}
                  proposal="OPEN"
                  saveUnchanged={detail.component.status === 'OPEN'}
                  canWrite={canWrite}
                  saving={saving}
                  saveLabel={t`Komponentenbewertung speichern`}
                  renderActions={(values, blocked) => (
                    <button type="button" className="ghost" disabled={blocked || detail.products.length === 0}
                      title={t`Speichert die Komponentenbewertung und bestätigt sie für die Produktversionen, die ihr folgen`}
                      onClick={() => void run(() => saveAndAdopt(event.id, values))}>
                      <Trans>Speichern und für alle Produktversionen übernehmen</Trans>
                    </button>
                  )}
                  onDirtyChange={dirtyHandlers.component}
                  onSave={(values) => void run(async () => {
                    const result = await submitComponentAssessment(event.id, values);
                    const status = STATUS_LABELS[result.status];
                    const saved = t`Komponentenbewertung gespeichert: ${status}.`;
                    if (!result.marked_for_review) return saved;
                    const marked = t`${plural(result.marked_for_review, { one: '# Produktbewertung', other: '# Produktbewertungen' })} zur erneuten Prüfung markiert.`;
                    return `${saved} ${marked}`;
                  })}
                />
                <details className="triage-context-details">
                  <summary><Trans>Schwachstelle & Umfang</Trans></summary>
                  <EventCveFacts event={event} />
                  <EventScopeFacts event={event} />
                </details>
              </section>
            </div>

            {/* --- Products ------------------------------------------------ */}
            <div role="tabpanel" id="assessment-panel-products" aria-labelledby="assessment-tab-products" hidden={tab !== 'products'}>
              {detail.products.length === 0 ? (
                <p className="muted"><Trans>Keine Produktversion enthält diese Komponentenversion.</Trans></p>
              ) : (
                <div className={detail.products.length > 1 ? 'assessment-split' : undefined}>
                  {detail.products.length > 1 ? <ul className="assessment-list" aria-label={t`Produktversionen`}>
                    {detail.products.map((p) => (
                      <li key={p.version_id}>
                        <button
                          type="button"
                          className={`assessment-list-item${p.version_id === openVersionId ? ' on' : ''}${verdictTint(p, p.version_id === openVersionId ? chosenStatus : null)}`}
                          aria-pressed={p.version_id === openVersionId}
                          onClick={() => setVersionId(p.version_id)}
                        >
                          <span className="assessment-list-title">
                            <strong>{productLabel(p)}</strong>
                            {p.custom ? <Chip tone="info"><Trans>Kundenversion</Trans></Chip> : null}
                          </span>
                          <ChipList>
                            <StatusChip status={p.effective_status} confirmed={p.confirmed} />
                            {reviewMark(p.needs_review, p.review_reason)}
                            {p.deviates ? <Chip title={t`Weicht vom Vorschlag der Komponentenbewertung ab`}><Trans>Abweichung</Trans></Chip> : null}
                            {p.contradiction ? <Chip tone="alert" title={t`Kunden wurden als betroffen benachrichtigt`}><Trans>Widerspruch</Trans></Chip> : null}
                            {advisoryOpen(p) ? <Chip tone="warn">{advisoryOpenLabel(p)}</Chip> : null}
                          </ChipList>
                          <span className="hint">{productListHint(p)}</span>
                        </button>
                      </li>
                    ))}
                  </ul> : null}
                  <KeepHeight resetKey={openVersionId}>
                    {product ? (
                      <section className="triage-doc-panel" aria-label={t`Bewertung ${openProductLabel}`}>
                        <div className="triage-panel-heading">
                          <h3>{productLabel(product)}</h3>
                          <StatusChip status={product.effective_status} confirmed={product.confirmed} />
                        </div>
                        {product.needs_review ? (
                          <Banner kind="warn" sub={t`Die bisherige Bewertung bleibt gültig, bis sie erneut gespeichert wird.`}>
                            <Trans>Erneut prüfen: {reviewReason}</Trans>
                          </Banner>
                        ) : null}
                        {product.contradiction ? (
                          <Banner kind="error">
                            <Trans>
                              Kunden wurden als betroffen benachrichtigt, die Produktbewertung lautet jetzt „{effectiveLabel}“.
                              Die versendeten Benachrichtigungen bleiben bestehen.
                            </Trans>
                          </Banner>
                        ) : null}
                        {product.assessed_at ? (
                          <AssessedLine at={product.assessed_at} by={product.assessed_by} />
                        ) : null}
                        <VerdictForm
                          key={`product-${product.version_id}-${revision}`}
                          level="product"
                          initial={{
                            status: product.status && product.status !== 'OPEN' ? product.status
                              : product.proposal === 'OPEN' ? 'UNDER_INVESTIGATION' : product.proposal,
                            justification: product.justification,
                            comment: product.comment ?? '',
                          }}
                          proposal={product.proposal}
                          canWrite={canWrite}
                          saving={saving}
                          saveLabel={productSaveLabel(product.confirmed, nextProduct !== null)}
                          saveUnchanged={!product.confirmed || product.needs_review}
                          onDirtyChange={dirtyHandlers.product}
                          onStatusChange={setChosenStatus}
                          onSave={(values) => void run(async () => productSavedMessage(
                            await submitProductAssessment(product.version_id, cve, values)))
                            .then((saved) => { if (saved && nextProduct !== null) setVersionId(nextProduct); })}
                        />
                        <ProductFollowUp
                          key={product.version_id}
                          product={product}
                          cve={cve}
                          canWrite={canWrite}
                          onDirtyChange={dirtyHandlers.documents}
                          item={cases.find((entry) => entry.product_type_id === product.product_type_id) ?? null}
                          onOpenCase={(caseId, section) => {
                            setCaseProductTypeId(product.product_type_id);
                            setCaseViews((current) => ({ ...current, [caseId]: { ...current[caseId], section } }));
                            setTab('case');
                          }}
                        />
                      </section>
                    ) : null}
                  </KeepHeight>
                </div>
              )}
            </div>

            {/* --- Vulnerability case (DEV-132) ---------------------------- */}
            <div role="tabpanel" id="assessment-panel-case" aria-labelledby="assessment-tab-case" hidden={tab !== 'case'}>
              <CaseTab
                cases={cases}
                revision={revision}
                productTypeId={caseProductTypeId}
                onSelect={setCaseProductTypeId}
                views={caseViews}
                onViewsChange={(caseId, view) => setCaseViews((current) => ({ ...current, [caseId]: view }))}
                openStage={initialReport}
                canWrite={canWrite}
                saving={saving}
                onRun={(action) => void run(action)}
                onDirtyChange={dirtyHandlers.case}
              />
            </div>

            {/* --- Instances ----------------------------------------------- */}
            <div role="tabpanel" id="assessment-panel-instances" aria-labelledby="assessment-tab-instances" hidden={tab !== 'instances'}>
              {instances.length === 0 ? (
                <p className="muted"><Trans>Keine Produktinstanz läuft auf einer betroffenen Produktversion.</Trans></p>
              ) : (
                <div className={instances.length > 1 ? 'assessment-split' : undefined}>
                  {instances.length > 1 ? <ul className="assessment-list" aria-label={t`Produktinstanzen`}>
                    {instances.map(({ instance, product: p }) => {
                      const serial = instance.serial ?? instance.instance_id;
                      const customer = instance.customer ?? t`ohne Kunde`;
                      const versionLabel = productLabel(p);
                      const priority = instance.priority ? PRIORITY_LABELS[instance.priority] : null;
                      return (
                        <li key={instance.instance_id}>
                          <button
                            type="button"
                            className={`assessment-list-item${instance.instance_id === selectedInstance?.instance.instance_id ? ' on' : ''}`}
                            aria-pressed={instance.instance_id === selectedInstance?.instance.instance_id}
                            onClick={() => setInstanceId(instance.instance_id)}
                          >
                            <span className="assessment-list-title">
                              <strong><Trans>S/N {serial}</Trans></strong>
                              <span className="muted">{customer} · {versionLabel}</span>
                            </span>
                            <ChipList>
                              <StatusChip status={instance.effective_status} confirmed={instance.confirmed} inherited={instance.inherited} />
                              {instance.priority ? <Chip tone={PRIORITY_TONE[instance.priority]}><Trans>Priorität {priority}</Trans></Chip> : null}
                              {reviewMark(instance.needs_review, instance.review_reason)}
                              {instance.version_changed ? <Chip tone="warn" title={t`Bewertet für eine andere Version als die aktuelle`}><Trans>Version gewechselt</Trans></Chip> : null}
                              {instance.deviates ? <Chip title={t`Weicht von der Produktbewertung ab`}><Trans>Abweichung</Trans></Chip> : null}
                            </ChipList>
                          </button>
                        </li>
                      );
                    })}
                  </ul> : null}
                  <KeepHeight resetKey={selectedInstance?.instance.instance_id ?? null}>
                    {selectedInstance ? (
                      <InstancePanel
                        key={`instance-${selectedInstance.instance.instance_id}-${revision}`}
                        instance={selectedInstance.instance}
                        productName={productLabel(selectedInstance.product)}
                        canWrite={canWrite}
                        saving={saving}
                        onDirtyChange={dirtyHandlers.instance}
                        onSave={(values, extra) => void run(async () => {
                          const result = await submitInstanceAssessment(selectedInstance.instance.instance_id, cve, { ...values, ...extra });
                          const status = STATUS_LABELS[result.status];
                          return t`Abweichung gespeichert: ${status}.`;
                        })}
                        onClear={() => void run(async () => {
                          await clearInstanceAssessment(selectedInstance.instance.instance_id, cve);
                          return t`Abweichung entfernt — die Instanz folgt wieder der Produktversion.`;
                        })}
                      />
                    ) : null}
                  </KeepHeight>
                </div>
              )}
            </div>
          </>
        ) : null}
      </div>
    </Modal>
  );
}

/* ------------------------------------------------------------------------- *
 * Below a product verdict: notifications, the case advisory and evidence
 * ------------------------------------------------------------------------- */

function ProductFollowUp({ product, item, cve, canWrite, onDirtyChange, onOpenCase }: Readonly<{
  product: ProductAssessmentRow;
  /** The vulnerability case of the product, once it exists. */
  item: VulnerabilityCase | null;
  cve: string;
  canWrite: boolean;
  onDirtyChange: (dirty: boolean) => void;
  /** Opens the case of the product at a step (DEV-103). */
  onOpenCase: (caseId: number, section: CaseSection) => void;
}>): ReactNode {
  if (!product.confirmed || (product.effective_status !== 'AFFECTED' && product.effective_status !== 'FIXED')) {
    return (
      <p className="hint">
        <Trans>Nachweise werden erfasst, sobald die Produktbewertung „Betroffen“ oder „Behoben“ bestätigt ist.</Trans>
      </p>
    );
  }
  return (
    <div className="assessment-follow-up">
      {/* DEV-103: the way on, in the vulnerability case — what is done, what comes next. */}
      {item ? (
        <div className="assessment-next">
          <span className="hint"><Trans>Weiter im Schwachstellenfall:</Trans></span>
          <CaseStepsLine item={item} onSelect={(section) => onOpenCase(item.id, section)} />
        </div>
      ) : null}
      <ProductDocuments versionId={product.version_id} cve={cve} canWrite={canWrite} onDirtyChange={onDirtyChange} />
    </div>
  );
}

/* ------------------------------------------------------------------------- *
 * Instance panel: verdict plus environment factors and priority
 * ------------------------------------------------------------------------- */

interface InstanceExtra {
  priority: AssessmentPriority | null;
  network_connected: boolean;
  remote_access: boolean;
  compensating_controls: boolean;
  environment_notes: string;
  customer_notice: string;
}

interface InstancePanelProps {
  instance: InstanceAssessmentRow;
  productName: string;
  canWrite: boolean;
  saving: boolean;
  onDirtyChange: (dirty: boolean) => void;
  onSave: (values: VerdictValues, extra: InstanceExtra) => void;
  /** Remove the deviation: the instance follows its product version again. */
  onClear: () => void;
}

/**
 * One instance (DEV-130): it follows the confirmed verdict of its product
 * version. The form only opens to record a deliberate deviation.
 */
function InstancePanel(props: Readonly<InstancePanelProps>): ReactNode {
  const { t } = useLingui();
  const { instance, productName, canWrite } = props;
  const serial = instance.serial ?? instance.instance_id;
  const customer = instance.customer ?? t`ohne Kunde`;
  const reviewReason = instance.review_reason ?? t`die Produktbewertung hat sich geändert`;
  const [editing, setEditing] = useState(instance.confirmed || instance.needs_review || instance.version_changed);
  const cancel = () => {
    props.onDirtyChange(false);
    setEditing(false);
  };
  return (
    <section className="triage-doc-panel" aria-label={t`Bewertung S/N ${serial}`}>
      <div className="triage-panel-heading">
        <h3><Trans>S/N {serial}</Trans> <span className="muted">· {customer} · {productName}</span></h3>
        <StatusChip status={instance.effective_status} confirmed={instance.confirmed} inherited={instance.inherited} />
      </div>
      {instance.needs_review ? (
        <Banner kind="warn" sub={t`Die Abweichung bleibt gültig, bis sie erneut gespeichert oder entfernt wird.`}>
          <Trans>Erneut prüfen: {reviewReason}</Trans>
        </Banner>
      ) : null}
      {instance.version_changed ? (
        <Banner kind="warn"><Trans>Die Instanz läuft inzwischen auf einer anderen Version; die frühere Bewertung gilt nur als Vorschlag.</Trans></Banner>
      ) : null}
      {editing ? (
        <InstanceDeviationForm {...props} onCancel={instance.confirmed ? null : cancel} />
      ) : (
        <InstanceFollows instance={instance} canWrite={canWrite} onDeviate={() => setEditing(true)} />
      )}
    </section>
  );
}

/** No own verdict: what the instance follows, and the way to record a deviation. */
function InstanceFollows({ instance, canWrite, onDeviate }: Readonly<{
  instance: InstanceAssessmentRow;
  canWrite: boolean;
  onDeviate: () => void;
}>): ReactNode {
  const { t } = useLingui();
  const status = STATUS_LABELS[instance.effective_status];
  return (
    <>
      <p className="hint">
        {instance.inherited
          ? t`Folgt der Produktbewertung „${status}“ — nichts zu bestätigen.`
          : t`Die Produktbewertung ist noch nicht bestätigt; die Instanz folgt ihr, sobald sie bestätigt ist.`}
        {' '}<Trans>
          Eine Abweichung nur bei einer bewussten Ausnahme erfassen (z. B. ohne Netzanbindung). Der Kunde wird
          über eine betroffene Produktversion in jedem Fall informiert.
        </Trans>
      </p>
      <div className="assessment-form-actions">
        <div className="spacer" />
        <button type="button" className="ghost" disabled={!canWrite}
          title={canWrite ? undefined : missingPermissionTitle('triage.write')} onClick={onDeviate}>
          <Trans>Abweichung erfassen</Trans>
        </button>
      </div>
    </>
  );
}

/** The stored deviation, else the proposal — an open or fixed proposal is no instance verdict. */
function initialInstanceStatus(instance: InstanceAssessmentRow): AssessmentStatus {
  if (instance.status && instance.status !== 'OPEN') return instance.status;
  return instance.proposal === 'OPEN' || instance.proposal === 'FIXED' ? 'UNDER_INVESTIGATION' : instance.proposal;
}

/** Verdict, environment factors and priority of a deliberate instance deviation. */
function InstanceDeviationForm({
  instance, canWrite, saving, onDirtyChange, onSave, onClear, onCancel,
}: Readonly<InstancePanelProps & { onCancel: (() => void) | null }>): ReactNode {
  const { t } = useLingui();
  const initialExtra: InstanceExtra = {
    priority: instance.priority,
    network_connected: instance.network_connected,
    remote_access: instance.remote_access,
    compensating_controls: instance.compensating_controls,
    environment_notes: instance.environment_notes ?? '',
    customer_notice: instance.customer_notice ?? '',
  };
  const [extra, setExtra] = useState<InstanceExtra>(initialExtra);
  const extraDirty = (Object.keys(initialExtra) as (keyof InstanceExtra)[])
    .some((key) => extra[key] !== initialExtra[key]);
  const factors: Array<{ key: 'network_connected' | 'remote_access' | 'compensating_controls'; label: string }> = [
    { key: 'network_connected', label: t`Netzanbindung` },
    { key: 'remote_access', label: t`Fernzugang` },
    { key: 'compensating_controls', label: t`Kompensationsmaßnahmen vorhanden` },
  ];

  return (
    <VerdictForm
      level="instance"
      initial={{
        status: initialInstanceStatus(instance),
        justification: instance.justification,
        comment: instance.comment ?? '',
      }}
      proposal={instance.proposal}
      canWrite={canWrite}
      saving={saving}
      saveLabel={t`Abweichung speichern`}
      extraDirty={extraDirty}
      saveUnchanged={instance.needs_review}
      renderActions={() => (onCancel ? (
        <button type="button" className="ghost" disabled={saving} onClick={onCancel}><Trans>Abbrechen</Trans></button>
      ) : (
        <button type="button" className="ghost" disabled={!canWrite || saving}
          title={t`Die Instanz folgt danach wieder der Produktversion`} onClick={onClear}>
          <Trans>Abweichung entfernen</Trans>
        </button>
      ))}
      onDirtyChange={onDirtyChange}
      onSave={(values) => onSave(values, extra)}
    >
      {(status) => (
        <>
          {status === 'NOT_AFFECTED' ? (
            <div className="field">
              <label htmlFor="instance-customer-notice"><Trans>Begründung für den Kunden</Trans></label>
              <textarea
                id="instance-customer-notice"
                rows={2}
                value={extra.customer_notice}
                onChange={(e) => setExtra((c) => ({ ...c, customer_notice: e.target.value }))}
                placeholder={t`z. B. Die Anlage wird nach Ihren Angaben ohne Netzwerkanbindung betrieben.`}
                aria-describedby="instance-customer-notice-hint"
              />
              <p className="hint" id="instance-customer-notice-hint">
                <Trans>
                  Erscheint in der Kundenmail bei dieser Anlage. Ohne Text steht dort der Standardsatz; die
                  interne Begründung oben bleibt intern.
                </Trans>
              </p>
            </div>
          ) : null}
          <div className="assessment-environment">
            <span className="assessment-environment-label"><Trans>Umgebungsfaktoren</Trans></span>
            {factors.map((factor) => (
              <label key={factor.key} className="check">
                <input
                  type="checkbox"
                  checked={extra[factor.key]}
                  onChange={(e) => setExtra((c) => ({ ...c, [factor.key]: e.target.checked }))}
                />
                {factor.label}
              </label>
            ))}
          </div>
          <div className="field">
            <label htmlFor="instance-environment-notes"><Trans>Anmerkungen zur Einsatzumgebung</Trans></label>
            <textarea
              id="instance-environment-notes"
              rows={2}
              value={extra.environment_notes}
              onChange={(e) => setExtra((c) => ({ ...c, environment_notes: e.target.value }))}
            />
          </div>
          <div className="field">
            <label htmlFor="instance-priority"><Trans>Priorität der Behebung</Trans></label>
            <select
              id="instance-priority"
              value={extra.priority ?? ''}
              onChange={(e) => setExtra((c) => ({ ...c, priority: (e.target.value || null) as AssessmentPriority | null }))}
            >
              <option value="">{t`— keine —`}</option>
              {(Object.keys(PRIORITY_LABELS) as AssessmentPriority[]).map((p) => (
                <option key={p} value={p}>{PRIORITY_LABELS[p]}</option>
              ))}
            </select>
          </div>
        </>
      )}
    </VerdictForm>
);
}

export default AssessmentDialog;
